/**
 * Every picture is prepared the same way before it is uploaded (2026-10-13): HEIC/HEIF photos
 * (iPhone) become JPEG, big photos are shrunk, and the result is a JPEG every browser can show.
 *
 * HEIC: the browser's own decoder is tried first (Safari can read HEIC); otherwise the heic2any
 * converter is loaded — only then, as a separate download, so it costs nothing for everyone else.
 */

export class UnreadableImageError extends Error {
  constructor() {
    super('This picture could not be read');
    this.name = 'UnreadableImageError';
  }
}

/** Is this an iPhone HEIC/HEIF photo? (type is often empty for these, so look at the name too) */
export function isHeic(file: { type?: string; name?: string }): boolean {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence' || type === 'image/heif-sequence') return true;
  return /\.(heic|heif)$/i.test(file.name || '');
}

/** Width/height that fit inside max × max, keeping the shape and never enlarging. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const ratio = Math.min(max / width, max / height, 1);
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
}

/**
 * Decode a picture the way browsers show it: an <img> applies the photo's orientation tag
 * (a phone photo held sideways), and drawing it onto a canvas keeps that. createImageBitmap has
 * handled the tag inconsistently (notably in Safari), so it is only the fallback (2026-10-13).
 */
async function decode(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new UnreadableImageError()); };
      img.src = url;
    });
  } catch {
    if (typeof createImageBitmap !== 'function') throw new UnreadableImageError();
    try {
      return await createImageBitmap(blob, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    } catch {
      throw new UnreadableImageError();
    }
  }
}

/** A JPEG (max `maxSize` px on the longest side) from any picture file the browser — or heic2any — can read. */
export async function prepareImageForUpload(file: File | Blob, maxSize = 1600, quality = 0.85): Promise<Blob> {
  let source: Blob = file;
  let picture: ImageBitmap | HTMLImageElement;
  try {
    picture = await decode(source);
  } catch {
    if (!isHeic(file as File)) throw new UnreadableImageError();
    try {
      const { default: heic2any } = await import('heic2any');
      const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality });
      source = Array.isArray(converted) ? converted[0] : converted;
      picture = await decode(source);
    } catch {
      throw new UnreadableImageError();
    }
  }
  const w = 'naturalWidth' in picture ? picture.naturalWidth : picture.width;
  const h = 'naturalHeight' in picture ? picture.naturalHeight : picture.height;
  if (!w || !h) throw new UnreadableImageError();
  const size = fitWithin(w, h, maxSize);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UnreadableImageError();
  ctx.drawImage(picture as CanvasImageSource, 0, 0, size.width, size.height);
  if ('close' in picture && typeof picture.close === 'function') picture.close();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new UnreadableImageError())), 'image/jpeg', quality);
  });
}

/** Size of the picture after turning it `quarterTurns` × 90° clockwise. */
export function rotatedSize(width: number, height: number, quarterTurns: number): { width: number; height: number } {
  return ((quarterTurns % 4) + 4) % 2 === 1 ? { width: height, height: width } : { width, height };
}

/** The picture turned `quarterTurns` × 90° clockwise, as a JPEG (2026-10-13: rotate in the task editor). */
export async function rotateImage(source: Blob, quarterTurns = 1, quality = 0.9): Promise<Blob> {
  const turns = ((quarterTurns % 4) + 4) % 4;
  const picture = await decode(source);
  const w = 'naturalWidth' in picture ? picture.naturalWidth : picture.width;
  const h = 'naturalHeight' in picture ? picture.naturalHeight : picture.height;
  if (!w || !h) throw new UnreadableImageError();
  const size = rotatedSize(w, h, turns);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new UnreadableImageError();
  ctx.translate(size.width / 2, size.height / 2);
  ctx.rotate((turns * Math.PI) / 2);
  ctx.drawImage(picture as CanvasImageSource, -w / 2, -h / 2, w, h);
  if ('close' in picture && typeof picture.close === 'function') picture.close();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new UnreadableImageError())), 'image/jpeg', quality);
  });
}
